import { Button, ButtonGroup, Checkbox, Field, RadioGroup, TextOutput } from 'Components/Core';
import { NamePrefixModifier, withInjectedProps } from 'Components/Core/InjectedPropsContext';
import { FieldSet, ListFieldSection } from 'Components/Layout';
import { IWithWorksheetInjectedProps } from 'Components/Worksheets/Core';
import { AdditionalVisualDescription, MaterialHistory } from 'Components/Worksheets/Core/Fields';
import { StabilizedMaterial } from 'Components/Worksheets/Core/Fields/StabilizedMaterial';
import { toMessageProps } from 'Components/Worksheets/Core/Helpers/messageHelpers';
import { Message } from 'Data/ViewModels/IApiDataModel';
import { valueOrDefault } from 'Lib/Any';
import { pipe } from 'Lib/Hooks/Selectors';
import deepEquals from 'Lib/Object/deepEquals';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { OversizeMaterialRemovedByType, OversizeSieveApertureSizeType, PreparationMethodType } from '../../../types';
import BsEnIsoPreparationAsReceivedWaterContentSection from './PreparationAsReceivedWaterContentSection';
import BsEnIsoPreparationMaterialMassSection from './PreparationMaterialMassSection';
import BsEnIsoPreparationTestPortionSection from './PreparationTestPortionSection';

let BsEnIsoPreparationSection: React.FC<IWithWorksheetInjectedProps> = ({ createWorksheetSelector }) => {
  const [testConditionsEditable, setTestConditionsEditable] = useState(false);

  const preparationMethod = useSelector(
    createWorksheetSelector!('Preparation.PreparationMethod'),
    deepEquals
  ) as PreparationMethodType;

  const noOversizeMaterial = useSelector(
    pipe(createWorksheetSelector!('Preparation.NoOversizeMaterial'), valueOrDefault(false))
  );
  const oversizeSieveApertureSize = useSelector(
    createWorksheetSelector!('Preparation.OversizeSieveApertureSize'),
    deepEquals
  );

  const oversizeSieveApertureSizeOptions = [
    { label: '0.4', value: OversizeSieveApertureSizeType.Sieve0_400mm },
    { label: '0.425', value: OversizeSieveApertureSizeType.Sieve0_425mm },
  ];

  const dryMassRetainedMessages = useSelector(
    pipe(
      createWorksheetSelector!('Preparation.Messages'),
      (m) => (m as Array<Message>)?.filter((m) => m.PropertyName === 'DryMassRetained'),
      (m) => m.map(toMessageProps)
    ),
    deepEquals
  );
  return (
    <NamePrefixModifier appendNamePrefix="Preparation">
      {/* Editable fields */}
      <ListFieldSection visible={testConditionsEditable}>
        <AdditionalVisualDescription editable />
        <Field input={<RadioGroup />} name="PreparationMethod" />
        <MaterialHistory editable />
        <StabilizedMaterial editable />
      </ListFieldSection>
      {/* Read-only fields */}
      {testConditionsEditable ? undefined : (
        <>
          <ListFieldSection>
            <AdditionalVisualDescription />
            <Field input={<TextOutput />} name="PreparationMethod" />
            <MaterialHistory />
            <StabilizedMaterial />
          </ListFieldSection>
          <ListFieldSection>
            <FieldSet>
              <ButtonGroup>
                <Button
                  onClick={() => {
                    setTestConditionsEditable(true);
                  }}
                >
                  Change
                </Button>
              </ButtonGroup>
            </FieldSet>
          </ListFieldSection>
        </>
      )}
      <ListFieldSection>
        {preparationMethod === PreparationMethodType.BS_1377_2 ||
        preparationMethod === PreparationMethodType.BS_1377_2_Clause_6 ? (
          <Field
            input={<TextOutput />}
            name="OversizeSieveApertureSize"
            value={oversizeSieveApertureSizeOptions.find((o) => o.value === oversizeSieveApertureSize)?.label}
          />
        ) : (
          <Field
            input={<RadioGroup />}
            name="OversizeSieveApertureSize"
            options={oversizeSieveApertureSizeOptions}
            simpleValue
          />
        )}
        <Field input={<Checkbox />} name="NoOversizeMaterial" />
        {!noOversizeMaterial && (
          <Field
            input={<RadioGroup />}
            name="OversizeMaterialRemovedBy"
            options={[
              { label: 'Hand', value: OversizeMaterialRemovedByType.Hand },
              { label: 'Wet Sieving', value: OversizeMaterialRemovedByType.WetSieving },
            ]}
          />
        )}
      </ListFieldSection>

      <BsEnIsoPreparationAsReceivedWaterContentSection />

      {!noOversizeMaterial && <BsEnIsoPreparationMaterialMassSection />}

      {dryMassRetainedMessages.length === 0 && <BsEnIsoPreparationTestPortionSection />}
    </NamePrefixModifier>
  );
};

BsEnIsoPreparationSection = withInjectedProps(BsEnIsoPreparationSection);

export { BsEnIsoPreparationSection };
export default BsEnIsoPreparationSection;
